import React from "react";
import { Button, Input, Select, Table, Tooltip } from "antd";
import { PlusCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Option } from "antd/es/mentions";

// Per-line task allocation panel shared by the Material Issue / Handover /
// Utilization screens. Tasks are stored as { key, taskId, taskBoqId, uomQty }
// in the parent's state, keyed by the parent line's row key.

const rowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "flex-end",
  padding: "10px 12px",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  marginBottom: 10,
  background: "#fafafa",
};

const TaskRow: React.FC<any> = ({ task, index, taskOptions, onUpdate, onRemove }) => (
  <div style={rowStyle}>
    <div style={{ fontWeight: 500, color: "#595959", minWidth: 28 }}>#{index + 1}</div>

    <div style={{ minWidth: 260, flex: "1 1 260px" }}>
      <div className="data-label mb-1">Task</div>
      <Select
        placeholder="Select Task"
        showSearch
        optionFilterProp="children"
        allowClear
        className="w-100"
        value={task.taskId}
        onChange={(val) => {
          const picked = taskOptions.find((t: any) => t.taskId === val);
          onUpdate({
            taskId: val ?? undefined,
            taskBoqId: picked?.taskBoqId ?? undefined,
          });
        }}
      >
        {taskOptions.map((t: any) => (
          <Option key={t.taskId} value={t.taskId}>
            {t.taskName}
            {t.boqName ? ` (${t.boqName})` : ""}
          </Option>
        ))}
      </Select>
    </div>

    <div style={{ minWidth: 140, flex: "0 1 140px" }}>
      <div className="data-label mb-1">Qty</div>
      <Input
        type="number"
        min={0}
        step={0.001}
        placeholder="Qty"
        className="w-100"
        value={task.uomQty}
        onChange={(e) => onUpdate({ uomQty: e.target.value })}
      />
    </div>

    <Tooltip title="Remove task">
      <Button danger type="text" icon={<DeleteOutlined />} onClick={onRemove} />
    </Tooltip>
  </div>
);

const formatQty = (val: any) => {
  if (val == null || val === "") return "--";
  const num = Number(val);
  return Number.isNaN(num) ? val : num.toLocaleString("en-IN");
};

const readOnlyTaskColumns = [
  {
    title: "#",
    key: "index",
    width: 44,
    align: "center" as const,
    render: (_: any, __: any, index: number) => index + 1,
  },
  {
    title: "Task",
    key: "taskName",
    render: (_: any, row: any) =>
      row?.taskName ||
      (row?.taskId != null ? `Task #${row.taskId}` : "--"),
  },
  {
    title: "BOQ Line",
    dataIndex: "boqLineName",
    key: "boqLineName",
    render: (val: any) => val || "--",
  },
  {
    title: "Issued Qty",
    dataIndex: "quantityIssued",
    key: "quantityIssued",
    align: "right" as const,
    render: (val: any) => formatQty(val),
  },
  {
    title: "Handover Status",
    dataIndex: "handoverStatus",
    key: "handoverStatus",
    render: (val: any) => val || "--",
  },
  {
    title: "Handovered Qty",
    dataIndex: "quantityHandovered",
    key: "quantityHandovered",
    align: "right" as const,
    render: (val: any) => formatQty(val),
  },
];

export interface MaterialTaskPanelProps {
  tasks: any[];
  taskOptions?: any[];
  readOnly?: boolean;
  onAddTask?: () => void;
  onUpdateTask?: (taskKey: string, changes: any) => void;
  onRemoveTask?: (taskKey: string) => void;
}

export const MaterialTaskPanel: React.FC<MaterialTaskPanelProps> = ({
  tasks,
  taskOptions = [],
  readOnly = false,
  onAddTask,
  onUpdateTask,
  onRemoveTask,
}) => {
  return (
    <div style={{ padding: "4px 8px 12px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span style={{ fontWeight: 600 }}>Task-wise Allocation</span>
        {!readOnly && onAddTask && (
          <Button type="dashed" icon={<PlusCircleOutlined />} onClick={onAddTask}>
            Add Task
          </Button>
        )}
      </div>

      {tasks.length === 0 ? (
        <div style={{ color: "#8c8c8c", fontStyle: "italic" }}>
          {readOnly
            ? "No tasks for this line."
            : "No tasks added. Click \"Add Task\" to allocate this material to a task."}
        </div>
      ) : readOnly ? (
        <Table
          columns={readOnlyTaskColumns}
          dataSource={tasks}
          rowKey={(row: any) =>
            row?.issueTaskId ?? row?.key ?? `${row?.taskId}-${row?.taskBoqId}`
          }
          pagination={false}
          size="small"
          bordered
          tableLayout="fixed"
          style={{ background: "#fff", borderRadius: 4 }}
        />
      ) : (
        tasks.map((task: any, index: number) => (
          <TaskRow
            key={task.key}
            task={task}
            index={index}
            taskOptions={taskOptions}
            onUpdate={(changes: any) => onUpdateTask?.(task.key, changes)}
            onRemove={() => onRemoveTask?.(task.key)}
          />
        ))
      )}
    </div>
  );
};

export const makeTaskKey = () =>
  `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default MaterialTaskPanel;
